import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CurrentToneCard = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTone, setCurrentTone] = useState('A♭ Major');
  const [planetaryInfluence, setPlanetaryInfluence] = useState('Venus ♂ Mars');
  const [intensity, setIntensity] = useState(78);

  const zodiacWheel = [
    { sign: '♈', name: 'Aries', angle: 0, active: false },
    { sign: '♉', name: 'Taurus', angle: 30, active: true },
    { sign: '♊', name: 'Gemini', angle: 60, active: false },
    { sign: '♋', name: 'Cancer', angle: 90, active: false },
    { sign: '♌', name: 'Leo', angle: 120, active: true },
    { sign: '♍', name: 'Virgo', angle: 150, active: false },
    { sign: '♎', name: 'Libra', angle: 180, active: false },
    { sign: '♏', name: 'Scorpio', angle: 210, active: false },
    { sign: '♐', name: 'Sagittarius', angle: 240, active: false },
    { sign: '♑', name: 'Capricorn', angle: 270, active: false },
    { sign: '♒', name: 'Aquarius', angle: 300, active: false },
    { sign: '♓', name: 'Pisces', angle: 330, active: true }
  ];

  const circleOfFifths = [
    { note: 'C', angle: 0, active: false },
    { note: 'G', angle: 30, active: true },
    { note: 'D', angle: 60, active: false },
    { note: 'A', angle: 90, active: false },
    { note: 'E', angle: 120, active: true },
    { note: 'B', angle: 150, active: false },
    { note: 'F♯', angle: 180, active: false },
    { note: 'C♯', angle: 210, active: false },
    { note: 'A♭', angle: 240, active: true },
    { note: 'E♭', angle: 270, active: false },
    { note: 'B♭', angle: 300, active: false },
    { note: 'F', angle: 330, active: false }
  ];

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const renderWheel = (items, radius, isOuter = true) => {
    return items.map((item, index) => {
      const angle = (item.angle * Math.PI) / 180;
      const x = 120 + radius * Math.cos(angle - Math.PI / 2);
      const y = 120 + radius * Math.sin(angle - Math.PI / 2);
      
      return (
        <g key={index}>
          <circle
            cx={x}
            cy={y}
            r={isOuter ? "16" : "12"}
            fill={item.active ? "#4A5FBF" : "#1A1B3A"}
            stroke={item.active ? "#8B5CF6" : "#2A2B4A"}
            strokeWidth="2"
            className={item.active ? "animate-gentle-pulse" : ""}
          />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-xs font-mono fill-current"
            fill={item.active ? "#F8FAFC" : "#94A3B8"}
          >
            {isOuter ? item.sign : item.note}
          </text>
        </g>
      );
    });
  };

  return (
    <div className="glass bg-surface/50 backdrop-blur-cosmic rounded-2xl p-6 shadow-elevation-3">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-text-primary">
            Your Current Tone
          </h2>
          <p className="text-sm text-text-secondary font-mono mt-1">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-heading font-bold text-accent">
            {currentTone}
          </div>
          <div className="text-xs text-text-secondary font-mono">
            {planetaryInfluence}
          </div>
        </div>
      </div>

      {/* Zodiac Wheel & Circle of Fifths Visualization */}
      <div className="relative mb-6">
        <svg width="240" height="240" className="mx-auto">
          {/* Background circles */}
          <circle
            cx="120"
            cy="120"
            r="100"
            fill="none"
            stroke="#2A2B4A"
            strokeWidth="1"
            strokeDasharray="4,4"
            opacity="0.5"
          />
          <circle
            cx="120"
            cy="120"
            r="70"
            fill="none"
            stroke="#2A2B4A"
            strokeWidth="1"
            strokeDasharray="4,4"
            opacity="0.5"
          />
          
          {/* Zodiac wheel (outer) */}
          {renderWheel(zodiacWheel, 100, true)}
          
          {/* Circle of fifths (inner) */}
          {renderWheel(circleOfFifths, 70, false)}
          
          {/* Center play button */}
          <circle
            cx="120"
            cy="120"
            r="25"
            fill="#4A5FBF"
            className="animate-cosmic-glow cursor-pointer"
            onClick={handlePlayPause}
          />
          <g transform="translate(120, 120)" className="cursor-pointer" onClick={handlePlayPause}>
            {isPlaying ? (
              <>
                <rect x="-6" y="-8" width="4" height="16" fill="white" />
                <rect x="2" y="-8" width="4" height="16" fill="white" />
              </>
            ) : (
              <polygon points="-6,-8 -6,8 8,0" fill="white" />
            )}
          </g>
        </svg>
        
        {/* Intensity indicator */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 bg-surface/80 backdrop-blur-sm rounded-full px-3 py-1">
          <div className="flex items-center space-x-2">
            <Icon name="Zap" size={14} color="#F59E0B" />
            <span className="text-xs font-mono text-accent">{intensity}%</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="primary"
          iconName={isPlaying ? "Pause" : "Play"}
          onClick={handlePlayPause}
          fullWidth
        >
          {isPlaying ? 'Pause' : 'Play'} My Tone
        </Button>
        <Button
          variant="outline"
          iconName="Settings"
          onClick={() => console.log('Customize tone')}
          fullWidth
        >
          Customize
        </Button>
      </div>

      {/* Current Planetary Positions */}
      <div className="mt-6 pt-4 border-t border-border/50">
        <h3 className="text-sm font-heading font-medium text-text-primary mb-3">
          Active Planetary Influences
        </h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <Icon name="Sun" size={20} color="#F59E0B" className="mx-auto mb-1" />
            <div className="text-xs font-mono text-text-secondary">♌ 15°</div>
          </div>
          <div className="text-center">
            <Icon name="Moon" size={20} color="#8B5CF6" className="mx-auto mb-1" />
            <div className="text-xs font-mono text-text-secondary">♓ 28°</div>
          </div>
          <div className="text-center">
            <Icon name="Sparkles" size={20} color="#EF4444" className="mx-auto mb-1" />
            <div className="text-xs font-mono text-text-secondary">♂ Rx</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentToneCard;